import { gsap } from "gsap";
import { Mesh, PlaneGeometry, type Scene, ShaderMaterial } from "three";
import EventEmitter from "./EventEmitter";

type EventTypes = "dispose";

export default class Loader extends EventEmitter<EventTypes> {
    readonly #overlayGeometry: PlaneGeometry;
    readonly #overlayMaterial: ShaderMaterial;
    readonly #overlay: Mesh;

    #scene: Scene;

    constructor(scene: Scene) {
        super();

        this.#scene = scene;
        this.#overlayGeometry = new PlaneGeometry(2, 2, 1, 1);
        this.#overlayMaterial = new ShaderMaterial({
            transparent: true,
            uniforms: {
                uOpacity: { value: 1 },
                uProgress: { value: 0 },
            },
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform float uOpacity;
                uniform float uProgress;
                varying vec2 vUv;

                void main() {
                    vec2 uv = vUv;

                    // 0.002 is the height of the bar
                    bool inBarY = abs(uv.y - 0.5) < 0.002 * 0.5;
                    bool inBarX = (uv.x <= uProgress) && (uv.x >= (uProgress - 1.0));

                    vec3 color = vec3(inBarY && inBarX ? 1.0 : 0.0);
                    gl_FragColor = vec4(color, uOpacity);
                }
            `,
        });
        this.#overlay = new Mesh(this.#overlayGeometry, this.#overlayMaterial);
        scene.add(this.#overlay);
    }

    onProgress = (progress: number): void => {
        gsap.to(this.#overlayMaterial.uniforms.uProgress, {
            value: progress,
            duration: 0.25,
            ease: "power2.inOut",
        });
    };

    onLoadComplete = (): void => {
        // - Animate progress bar to 100% and then out of view
        // - Fade out the overlay
        // - Dispose of the overlay and all references
        gsap.timeline({
            onComplete: () => {
                this.#overlay.visible = false;
                this.#overlayGeometry.dispose();
                this.#overlayMaterial.dispose();
                this.#scene.remove(this.#overlay);
                this.trigger("dispose");
            },
        })
            .to(this.#overlayMaterial.uniforms.uProgress, {
                value: 2,
                duration: 1,
                ease: "power2.inOut",
            })
            .to(this.#overlayMaterial.uniforms.uOpacity, {
                value: 0,
                duration: 0.5,
                ease: "power2.inOut",
            });
    };
}
